import classNames from 'classnames';
import {
  type CSSProperties,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import {
  BAR_END_HOVER_SLOP,
  CHART_HEADER_LABEL_HEIGHT,
  CHART_LABEL_GAP,
  ReplotChartNames,
} from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, useChartTheme } from '../../utilities';
import {
  getDimmedPlotColor,
  getPrimaryColor,
  getPrimaryColorDark,
} from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { getCursorPosition } from '../plot-tooltip/cursor-tooltip';
import {
  PlotTooltip,
  type TooltipPosition,
} from '../plot-tooltip/plot-tooltip';
import styles from './sankey-chart.module.css';
import { DEFAULT_INVERSION_TOLERANCE } from './sankey-inversions';
import { LINEAR_SANKEY_SCALE } from './sankey-layout';
import { SankeyLinkTooltipContent } from './sankey-link-tooltip';
import { INTERIOR_LABEL_GAP, NodeLabel } from './sankey-node-label';
import { SankeyNodeTooltipContent } from './sankey-node-tooltip';
import {
  getSankeyNodeLabel,
  type SankeyData,
  type SankeyRenderLink,
  type SankeyScale,
} from './types';
import { PLOT_BOTTOM_PADDING, useSankeyLayout } from './use-sankey-layout';

const DEFAULT_NODE_COLOR = 'var(--ui-text-normal, #2d426a)';

export type SankeyChartProps = {
  data: SankeyData | null;
  color?: string;
  nodeColor?: string;
  showNodeLabels?: boolean;
  showNodeValues?: boolean;
  showLinkValues?: boolean;
  scale?: SankeyScale;
  autoScale?: boolean;
  showOrderInversions?: boolean;
  inversionTolerance?: number;
  /** Re-space columns to fill the plot height and spread unbalanced ribbons. */
  fillColumnHeight?: boolean;
  leftFlowLabel?: string;
  rightFlowLabel?: string;
} & CommonPlotProps;

const format = getFormatter(FormatType.INTEGER);

const INVERSION_OUTLINE_PX = 1.5;
const LINK_VALUE_FONT_SIZE = 12;
const LINK_VALUE_MIN_GAP_X = 38;
const LINK_VALUE_MIN_GAP_Y = 14;
const LINK_VALUE_SLOTS = [0.5, 0.34, 0.66, 0.22, 0.78];
const FLOW_LABEL_HEIGHT = CHART_HEADER_LABEL_HEIGHT;
const FLOW_LABEL_GAP = CHART_LABEL_GAP;
const FLOW_LABEL_FONT_SIZE = 12;
const NODE_LABEL_FONT_SIZE = 14;

type HoveredLink = { key: string; group?: string };

const linkDomId = (instanceId: string, index: number): string =>
  `${instanceId}-link-${index}`;

const isLinkHighlighted = (
  link: SankeyRenderLink,
  hovered: HoveredLink | null
): boolean => {
  if (hovered === null) return false;
  if (hovered.group !== undefined) return link.group === hovered.group;
  return link.key === hovered.key;
};

const pointAlongLink = (
  link: SankeyRenderLink,
  t: number
): { x: number; y: number } => {
  const inverse = 1 - t;
  const controlX = (link.sourceX + link.targetX) / 2;
  return {
    x:
      inverse ** 3 * link.sourceX +
      3 * inverse * t * controlX +
      t ** 3 * link.targetX,
    y: link.sourceY + (link.targetY - link.sourceY) * (t * t * (3 - 2 * t)),
  };
};

type ValueLabel = { link: SankeyRenderLink; x: number; y: number };

const placeValueLabels = (links: SankeyRenderLink[]): ValueLabel[] => {
  const placed: ValueLabel[] = [];
  for (const link of [...links].sort((a, b) => b.rawValue - a.rawValue)) {
    for (const t of LINK_VALUE_SLOTS) {
      const { x, y } = pointAlongLink(link, t);
      const collides = placed.some(
        (label) =>
          Math.abs(label.x - x) < LINK_VALUE_MIN_GAP_X &&
          Math.abs(label.y - y) < LINK_VALUE_MIN_GAP_Y
      );
      if (collides) continue;
      placed.push({ link, x, y });
      break;
    }
  }
  return placed;
};

/** Multi-level Sankey driven entirely by graph topology. */
export const SankeyChart = ({
  data,
  color: colorProp,
  nodeColor: nodeColorProp,
  showNodeLabels = true,
  showNodeValues = false,
  showLinkValues = false,
  scale = LINEAR_SANKEY_SCALE,
  autoScale = false,
  showOrderInversions = false,
  inversionTolerance = DEFAULT_INVERSION_TOLERANCE,
  fillColumnHeight = true,
  leftFlowLabel,
  rightFlowLabel,
  loading = false,
  className,
}: SankeyChartProps) => {
  const { theme } = useChartTheme();
  const isDarkTheme = theme === 'dark';
  const labelFontSize = NODE_LABEL_FONT_SIZE;
  const interiorLabelReserve = showNodeLabels
    ? INTERIOR_LABEL_GAP + labelFontSize
    : 0;
  const flowLabelLeft = leftFlowLabel?.trim() ?? '';
  const flowLabelRight = rightFlowLabel?.trim() ?? '';
  const hasFlowLabels = flowLabelLeft !== '' || flowLabelRight !== '';
  const bottomReserve = hasFlowLabels
    ? Math.max(0, FLOW_LABEL_HEIGHT + FLOW_LABEL_GAP - PLOT_BOTTOM_PADDING)
    : 0;
  const defaultNodeColor = isDarkTheme ? '#e2e8f0' : DEFAULT_NODE_COLOR;
  const _nodeColor = nodeColorProp ?? defaultNodeColor;
  const color =
    colorProp ?? (isDarkTheme ? getPrimaryColorDark() : getPrimaryColor());
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(containerRef);
  // Sanitised (no colons) so it is a valid id / `#` fragment for `<use>`.
  const instanceId = useId().replace(/:/g, '');

  const [hovered, setHovered] = useState<HoveredLink | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const trackCursor = useCallback(
    (event: { clientX: number; clientY: number }) =>
      setCursor(getCursorPosition(event)),
    []
  );

  const hasData = !!data && data.nodes.length > 0 && data.links.length > 0;

  const {
    isPlotSizeValid,
    maxDepth,
    nodesValidated,
    renderLinks,
    rawByNode,
    interiorLabelMaxWidth,
    sideLabelMaxWidth,
    invertedLinkKeys,
    invertedNodeIds,
  } = useSankeyLayout({
    data,
    width,
    height,
    scale,
    autoScale,
    fillColumnHeight,
    interiorLabelReserve,
    bottomReserve,
    showOrderInversions,
    inversionTolerance,
    labelFontSize: showNodeLabels ? labelFontSize : null,
    formatNodeValue: showNodeValues ? format : undefined,
  });

  // Base ribbons: hover-independent and memoized; highlight lives in the overlay.
  const renderedLinkPaths = useMemo(
    () =>
      renderLinks.map((link) => (
        <path
          key={link.key}
          id={linkDomId(instanceId, link.index)}
          d={link.path}
          fill="none"
          stroke={link.color ?? color}
          strokeWidth={link.width}
          style={
            {
              '--replots-dim-color': getDimmedPlotColor(
                link.color ?? color,
                theme
              ),
            } as CSSProperties
          }
          className={styles.path}
          data-testid="sankey-chart__path"
          onMouseEnter={() => setHovered({ key: link.key, group: link.group })}
          onMouseMove={trackCursor}
          onMouseLeave={() => {
            setHovered(null);
            setCursor(null);
          }}
        />
      )),
    [renderLinks, color, instanceId, theme, trackCursor]
  );

  const renderedLinkInversions = useMemo(() => {
    if (!showOrderInversions) return null;
    return renderLinks
      .filter((link) => invertedLinkKeys.has(link.key))
      .map((link) => (
        <path
          key={link.key}
          d={link.path}
          fill="none"
          strokeWidth={link.width + INVERSION_OUTLINE_PX * 2}
          className={styles.pathInverted}
          data-testid="sankey-link-inversion"
        />
      ));
  }, [renderLinks, showOrderInversions, invertedLinkKeys]);

  // Node hit padding, vertically capped at half the gap to the column
  // neighbour so stacked nodes don't steal from each other.
  const nodeHitSlops = useMemo(() => {
    const byDepth = new Map<number, typeof nodesValidated>();
    for (const node of nodesValidated) {
      const column = byDepth.get(node.depth);
      if (column) column.push(node);
      else byDepth.set(node.depth, [node]);
    }
    const slops = new Map<string, { top: number; bottom: number }>();
    for (const column of byDepth.values()) {
      const ordered = [...column].sort((a, b) => a.y0 - b.y0);
      ordered.forEach((node, index) => {
        const previous = ordered[index - 1];
        const next = ordered[index + 1];
        const gapAbove =
          previous === undefined
            ? Number.POSITIVE_INFINITY
            : node.y0 - previous.y1;
        const gapBelow =
          next === undefined ? Number.POSITIVE_INFINITY : next.y0 - node.y1;
        slops.set(node.id, {
          top: Math.min(BAR_END_HOVER_SLOP, Math.max(0, gapAbove / 2)),
          bottom: Math.min(BAR_END_HOVER_SLOP, Math.max(0, gapBelow / 2)),
        });
      });
    }
    return slops;
  }, [nodesValidated]);

  const renderedNodes = useMemo(
    () =>
      nodesValidated.map((node) => {
        const isFirstColumn = node.depth === 0;
        const isLastColumn = node.depth === maxDepth;
        const centerY = (node.y0 + node.y1) / 2;
        const raw = rawByNode.get(node.id) ?? { in: 0, out: 0 };
        const rawTotal = Math.max(raw.in, raw.out);
        const slop = nodeHitSlops.get(node.id) ?? { top: 0, bottom: 0 };
        return (
          <g key={node.id}>
            {/* Padded hover target — nodes render as empty space. */}
            <rect
              x={node.x0 - BAR_END_HOVER_SLOP}
              y={node.y0 - slop.top}
              width={node.x1 - node.x0 + BAR_END_HOVER_SLOP * 2}
              height={Math.max(node.y1 - node.y0, 1) + slop.top + slop.bottom}
              rx={1}
              fill="transparent"
              className={styles.node}
              onMouseEnter={() => setHoveredNodeId(node.id)}
              onMouseMove={trackCursor}
              onMouseLeave={() => {
                setHoveredNodeId(null);
                setCursor(null);
              }}
            />
            {showNodeLabels && (
              <NodeLabel
                node={node}
                centerY={centerY}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
                sideLabelMaxWidth={sideLabelMaxWidth}
                interiorLabelMaxWidth={interiorLabelMaxWidth}
                label={getSankeyNodeLabel(node)}
                value={showNodeValues ? format(rawTotal) : undefined}
                fontSize={labelFontSize}
                hoverProps={{
                  onMouseEnter: () => setHoveredNodeId(node.id),
                  onMouseMove: trackCursor,
                  onMouseLeave: () => {
                    setHoveredNodeId(null);
                    setCursor(null);
                  },
                }}
              />
            )}
          </g>
        );
      }),
    [
      nodesValidated,
      nodeHitSlops,
      maxDepth,
      rawByNode,
      showNodeLabels,
      showNodeValues,
      sideLabelMaxWidth,
      interiorLabelMaxWidth,
      trackCursor,
    ]
  );

  const renderedNodeInversions = useMemo(() => {
    if (!showOrderInversions) return null;
    return nodesValidated
      .filter((node) => invertedNodeIds.has(node.id))
      .map((node) => (
        <rect
          key={node.id}
          x={node.x0 - INVERSION_OUTLINE_PX}
          y={node.y0 - INVERSION_OUTLINE_PX}
          width={node.x1 - node.x0 + INVERSION_OUTLINE_PX * 2}
          height={Math.max(node.y1 - node.y0, 1) + INVERSION_OUTLINE_PX * 2}
          className={styles.nodeInverted}
          data-testid="sankey-node-inversion"
        />
      ));
  }, [nodesValidated, showOrderInversions, invertedNodeIds]);

  const renderedLinkValues = useMemo(() => {
    if (!showLinkValues) return null;
    const labelled = showOrderInversions
      ? renderLinks.filter((link) => invertedLinkKeys.has(link.key))
      : renderLinks;
    return placeValueLabels(labelled).map(({ link, x, y }) => (
      <text
        key={link.key}
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={LINK_VALUE_FONT_SIZE}
        className={classNames(
          styles.linkValue,
          showOrderInversions && styles.linkValueInverted
        )}
        data-testid="sankey-link-value"
      >
        {format(link.rawValue)}
      </text>
    ));
  }, [renderLinks, showLinkValues, showOrderInversions, invertedLinkKeys]);

  const renderedFlowLabels = useMemo(() => {
    if (!hasFlowLabels || !isPlotSizeValid) return null;
    const columnCenters = new Map<number, number>();
    for (const node of nodesValidated) {
      if (columnCenters.has(node.depth)) continue;
      columnCenters.set(node.depth, (node.x0 + node.x1) / 2);
    }
    const bandCenter = (from: number, to: number): number | undefined => {
      const start = columnCenters.get(from);
      const end = columnCenters.get(to);
      if (start === undefined || end === undefined) return undefined;
      return (start + end) / 2;
    };
    const [leftX, rightX] =
      maxDepth >= 2
        ? [bandCenter(0, 1), bandCenter(maxDepth - 1, maxDepth)]
        : [columnCenters.get(0), columnCenters.get(maxDepth)];
    const y = height - FLOW_LABEL_HEIGHT / 2;
    return [
      { side: 'left', label: flowLabelLeft, x: leftX },
      { side: 'right', label: flowLabelRight, x: rightX },
    ].flatMap(({ side, label, x }) =>
      label === '' || x === undefined
        ? []
        : [
            <text
              key={side}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={FLOW_LABEL_FONT_SIZE}
              className={styles.flowLabel}
              data-testid={`sankey-flow-label-${side}`}
            >
              {label}
            </text>,
          ]
    );
  }, [
    hasFlowLabels,
    flowLabelLeft,
    flowLabelRight,
    height,
    isPlotSizeValid,
    maxDepth,
    nodesValidated,
  ]);

  // Hover overlay: only this re-renders as the pointer moves.
  const highlightedLinks = useMemo(
    () => renderLinks.filter((link) => isLinkHighlighted(link, hovered)),
    [renderLinks, hovered]
  );
  const hoveredLink = hovered
    ? renderLinks.find((link) => link.key === hovered.key)
    : undefined;
  const hoveredNode = hoveredNodeId
    ? nodesValidated.find((node) => node.id === hoveredNodeId)
    : undefined;

  // One lightweight tooltip, following the cursor over whichever ribbon/node
  // is hovered.
  const tooltipContent = (() => {
    if (hoveredLink) {
      return <SankeyLinkTooltipContent link={hoveredLink} format={format} />;
    }
    if (hoveredNode) {
      return (
        <SankeyNodeTooltipContent
          node={hoveredNode}
          format={format}
          raw={rawByNode.get(hoveredNode.id) ?? { in: 0, out: 0 }}
        />
      );
    }
    return null;
  })();

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.Sankey)}
    >
      <PlotLoadWrapper loading={loading} noData={!hasData}>
        <svg width="100%" height="100%">
          {isPlotSizeValid && (
            <g id="render-container">
              {renderedFlowLabels && (
                <g id="flow-labels">{renderedFlowLabels}</g>
              )}
              {renderedLinkInversions && (
                <g id="link-inversions">{renderedLinkInversions}</g>
              )}
              {/* Desaturate the non-hovered ribbons while one is hovered;
                  the highlight overlay below stays in full color. */}
              <g
                id="links"
                className={hovered !== null ? styles.linksDimmed : undefined}
              >
                {renderedLinkPaths}
              </g>
              <g id="nodes">{renderedNodes}</g>
              {/* `<use>` re-references existing paths so hovering stays cheap. */}
              <g id="sankey-hover-layer" style={{ pointerEvents: 'none' }}>
                {highlightedLinks.map((link) => (
                  <use
                    key={link.key}
                    href={`#${linkDomId(instanceId, link.index)}`}
                    className={styles.pathHover}
                  />
                ))}
              </g>
              {renderedNodeInversions && (
                <g id="node-inversions">{renderedNodeInversions}</g>
              )}
              {renderedLinkValues && (
                <g id="link-values">{renderedLinkValues}</g>
              )}
            </g>
          )}
        </svg>
      </PlotLoadWrapper>
      {tooltipContent && cursor && (
        <PlotTooltip
          isVisible
          x={cursor.x}
          y={cursor.y}
          placement="vertical"
          space="viewport"
        >
          {tooltipContent}
        </PlotTooltip>
      )}
    </div>
  );
};
